"use client"

import { useState, useEffect } from "react"
import styled from "styled-components"
import { Save, Upload, ArrowLeft, Trash2, AlertTriangle } from "lucide-react"

// 스타일드 컴포넌트 정의
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const BackLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    color: #0f172a;
  }
`

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
`

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #fee2e2;
  color: #ef4444;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #fecaca;
  }
`

const Grid = styled.div`
  display: grid;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`

const Card = styled.div`
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid #e2e8f0;
`

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
`

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
`

const CardContent = styled.div`
  padding: 1.5rem;
`

const ImageUploadArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ImagePreviewContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background-color: #f1f5f9;
  border-radius: 0.5rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #94a3b8;
`

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const UploadPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  color: #64748b;
`

const FileInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.875rem;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #0f172a;
`

const Input = styled.input`
  width: calc(100% - 0.75rem);
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #94a3b8;
    box-shadow: 0 0 0 1px #94a3b8;
  }
`

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #94a3b8;
    box-shadow: 0 0 0 1px #94a3b8;
  }
`

const Select = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #94a3b8;
    box-shadow: 0 0 0 1px #94a3b8;
  }
`

const InputGroup = styled.div`
  position: relative;
  
  & > span {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
    pointer-events: none;
  }
`

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e2e8f0;
  margin: 1.5rem 0;
`

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: ${(props) => (props.secondary ? "white" : "#0f172a")};
  color: ${(props) => (props.secondary ? "#0f172a" : "white")};
  border: ${(props) => (props.secondary ? "1px solid #e2e8f0" : "none")};
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${(props) => (props.secondary ? "#f8fafc" : "#1e293b")};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`

const SpinnerSvg = styled.svg`
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #0f172a;
  margin: 1.5rem 0 1rem;
`

const CurriculumSection = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  overflow: hidden;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
`

const SectionHeaderInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  
  &:focus {
    outline: none;
  }
`

const SectionActions = styled.div`
  display: flex;
  gap: 0.5rem;
`

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.25rem;
  border: none;
  background-color: ${(props) => (props.danger ? "#fee2e2" : "#f1f5f9")};
  color: ${(props) => (props.danger ? "#ef4444" : "#64748b")};
  cursor: pointer;
  
  &:hover {
    background-color: ${(props) => (props.danger ? "#fecaca" : "#e2e8f0")};
  }
`

const SectionContent = styled.div`
  padding: 1rem;
`

const LessonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const LessonItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  background-color: white;
`

const LessonInput = styled.input`
  flex: 1;
  border: none;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
  }
`

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem;
  background-color: #f1f5f9;
  color: #64748b;
  border: 1px dashed #cbd5e1;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  
  &:hover {
    background-color: #e2e8f0;
  }
`

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
`

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ModalBody = styled.div`
  padding: 1.5rem;
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
`

// 관리자용 강의 수정 페이지 컴포넌트
function CourseEdit() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    originalPrice: "",
    category: "",
    level: "",
    duration: "",
  })
  const [curriculum, setCurriculum] = useState([
    {
      title: "",
      lessons: [{ title: "", duration: "" }],
    },
  ])

  // 페이지 로드 시 기존 강의 데이터 불러오기 (예시)
  useEffect(() => {
    // API 호출 시뮬레이션
    setTimeout(() => {
      const courseData = {
        title: "React 완벽 가이드",
        description:
          "React의 기초부터 고급 개념까지 모두 배울 수 있는 완벽한 가이드입니다. 컴포넌트, 상태 관리, 라우팅, 서버 통신 등 실무에 필요한 모든 내용을 다룹니다.",
        price: "49000",
        originalPrice: "69000",
        category: "programming",
        level: "intermediate",
        duration: "20시간 30분",
        image: "https://via.placeholder.com/800x450",
        curriculum: [
          {
            title: "섹션 1: React 소개",
            lessons: [
              { title: "React란 무엇인가?", duration: "12:30" },
              { title: "개발 환경 설정", duration: "15:45" },
              { title: "첫 번째 React 앱 만들기", duration: "20:10" },
            ],
          },
          {
            title: "섹션 2: React 기초",
            lessons: [
              { title: "JSX 문법", duration: "18:20" },
              { title: "컴포넌트와 Props", duration: "22:15" },
              { title: "상태(State)와 생명주기", duration: "25:30" },
              { title: "이벤트 처리하기", duration: "15:45" },
            ],
          },
        ],
      }

      setFormData({
        title: courseData.title,
        description: courseData.description,
        price: courseData.price,
        originalPrice: courseData.originalPrice,
        category: courseData.category,
        level: courseData.level,
        duration: courseData.duration,
      })

      setCurriculum(courseData.curriculum)
      setImagePreview(courseData.image)
    }, 500)
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleImageChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSectionChange = (index, value) => {
    const newCurriculum = [...curriculum]
    newCurriculum[index].title = value
    setCurriculum(newCurriculum)
  }

  const handleLessonChange = (sectionIndex, lessonIndex, field, value) => {
    const newCurriculum = [...curriculum]
    newCurriculum[sectionIndex].lessons[lessonIndex][field] = value
    setCurriculum(newCurriculum)
  }

  const addSection = () => {
    setCurriculum([...curriculum, { title: "새 섹션", lessons: [{ title: "", duration: "" }] }])
  }

  const removeSection = (index) => {
    const newCurriculum = [...curriculum]
    newCurriculum.splice(index, 1)
    setCurriculum(newCurriculum)
  }

  const addLesson = (sectionIndex) => {
    const newCurriculum = [...curriculum]
    newCurriculum[sectionIndex].lessons.push({ title: "", duration: "" })
    setCurriculum(newCurriculum)
  }

  const removeLesson = (sectionIndex, lessonIndex) => {
    const newCurriculum = [...curriculum]
    newCurriculum[sectionIndex].lessons.splice(lessonIndex, 1)
    setCurriculum(newCurriculum)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    // API 호출 시뮬레이션
    setTimeout(() => {
      setIsSubmitting(false)
      alert("강의가 성공적으로 수정되었습니다.")
    }, 1500)
  }

  const handleDelete = () => {
    // API 호출 시뮬레이션
    setTimeout(() => {
      alert("강의가 삭제되었습니다.")
      setShowDeleteModal(false)
      // 여기서 리다이렉트 처리
    }, 1000)
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackLink href="/admin/courses">
            <ArrowLeft size={20} />
            강의 목록
          </BackLink>
          <Title>강의 수정</Title>
        </HeaderLeft>
        <DeleteButton onClick={() => setShowDeleteModal(true)}>
          <Trash2 size={16} />
          강의 삭제
        </DeleteButton>
      </Header>

      <Form onSubmit={handleSubmit}>
        <Grid>
          <Card>
            <CardHeader>
              <CardTitle>강의 이미지</CardTitle>
            </CardHeader>
            <CardContent>
              <ImageUploadArea>
                <ImagePreviewContainer>
                  {imagePreview ? (
                    <ImagePreview src={imagePreview || "/placeholder.svg"} alt="Preview" />
                  ) : (
                    <UploadPlaceholder>
                      <Upload size={40} style={{ marginBottom: "0.5rem" }} />
                      <p style={{ fontSize: "0.875rem" }}>이미지를 드래그하거나 클릭하여 업로드하세요</p>
                      <p style={{ fontSize: "0.75rem", marginTop: "0.25rem" }}>PNG, JPG, GIF 최대 5MB</p>
                    </UploadPlaceholder>
                  )}
                </ImagePreviewContainer>
                <FileInput type="file" accept="image/*" onChange={handleImageChange} />
              </ImageUploadArea>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>강의 정보</CardTitle>
            </CardHeader>
            <CardContent>
              <FormGroup>
                <Label htmlFor="title">강의명</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="강의명을 입력하세요"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="description">강의 설명</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="강의에 대한 상세 설명을 입력하세요"
                  required
                />
              </FormGroup>

              <TwoColumnGrid>
                <FormGroup>
                  <Label htmlFor="price">판매 가격</Label>
                  <InputGroup>
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                      required
                    />
                    <span>원</span>
                  </InputGroup>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="originalPrice">정가</Label>
                  <InputGroup>
                    <Input
                      id="originalPrice"
                      name="originalPrice"
                      type="number"
                      value={formData.originalPrice}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                    />
                    <span>원</span>
                  </InputGroup>
                </FormGroup>
              </TwoColumnGrid>

              <TwoColumnGrid>
                <FormGroup>
                  <Label htmlFor="category">카테고리</Label>
                  <Select id="category" name="category" value={formData.category} onChange={handleChange} required>
                    <option value="" disabled>
                      카테고리 선택
                    </option>
                    <option value="programming">프로그래밍</option>
                    <option value="graphics">그래픽</option>
                    <option value="database">데이터베이스</option>
                    <option value="devops">데브옵스</option>
                    <option value="infra">인프라</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="level">난이도</Label>
                  <Select id="level" name="level" value={formData.level} onChange={handleChange} required>
                    <option value="" disabled>
                      난이도 선택
                    </option>
                    <option value="beginner">입문</option>
                    <option value="intermediate">초급</option>
                    <option value="advanced">중급</option>
                    <option value="expert">고급</option>
                  </Select>
                </FormGroup>
              </TwoColumnGrid>

              <FormGroup>
                <Label htmlFor="duration">강의 시간</Label>
                <Input
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  placeholder="예: 8시간 30분"
                  required
                />
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        <Card>
          <CardHeader>
            <CardTitle>커리큘럼</CardTitle>
          </CardHeader>
          <CardContent>
            {curriculum.map((section, sectionIndex) => (
              <CurriculumSection key={sectionIndex}>
                <SectionHeader>
                  <SectionHeaderInput
                    value={section.title}
                    onChange={(e) => handleSectionChange(sectionIndex, e.target.value)}
                    placeholder="섹션 제목"
                  />
                  <SectionActions>
                    <IconButton danger onClick={() => removeSection(sectionIndex)}>
                      <Trash2 size={16} />
                    </IconButton>
                  </SectionActions>
                </SectionHeader>
                <SectionContent>
                  <LessonList>
                    {section.lessons.map((lesson, lessonIndex) => (
                      <LessonItem key={lessonIndex}>
                        <LessonInput
                          value={lesson.title}
                          onChange={(e) => handleLessonChange(sectionIndex, lessonIndex, "title", e.target.value)}
                          placeholder="강의 제목"
                        />
                        <LessonInput
                          value={lesson.duration}
                          onChange={(e) => handleLessonChange(sectionIndex, lessonIndex, "duration", e.target.value)}
                          placeholder="시간 (예: 10:30)"
                          style={{ maxWidth: "100px" }}
                        />
                        <IconButton danger onClick={() => removeLesson(sectionIndex, lessonIndex)}>
                          <Trash2 size={16} />
                        </IconButton>
                      </LessonItem>
                    ))}
                  </LessonList>
                  <AddButton type="button" onClick={() => addLesson(sectionIndex)}>
                    + 강의 추가
                  </AddButton>
                </SectionContent>
              </CurriculumSection>
            ))}

            <AddButton type="button" onClick={addSection}>
              + 새 섹션 추가
            </AddButton>
          </CardContent>
        </Card>

        <Divider />

        <ButtonGroup>
          <Button type="button" secondary>
            취소
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <SpinnerSvg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </SpinnerSvg>
                저장 중...
              </>
            ) : (
              <>
                <Save size={16} />
                변경사항 저장
              </>
            )}
          </Button>
        </ButtonGroup>
      </Form>

      {showDeleteModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                <AlertTriangle size={20} color="#ef4444" />
                강의 삭제
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              <p>정말로 이 강의를 삭제하시겠습니까?</p>
              <p>이 작업은 되돌릴 수 없으며, 모든 강의 데이터가 영구적으로 삭제됩니다.</p>
            </ModalBody>
            <ModalFooter>
              <Button secondary onClick={() => setShowDeleteModal(false)}>
                취소
              </Button>
              <Button style={{ backgroundColor: "#ef4444" }} onClick={handleDelete}>
                <Trash2 size={16} />
                삭제
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Container>
  )
}

export default CourseEdit